import i18n from '@/lang/i18n'
import { DescriptionRequest } from '@/modules/configurationModule/detail/local-samples/preformatted/services/preformatted.dto'
import PreformattedEditorActionClearTemplates from '@/modules/configurationModule/detail/local-samples/preformatted/shared/components/preformatted-editor-action-clear-template/preformatted-editor-action-clear-template.vue'
import PreformattedEditorActionClearTemplatesComponent from '@/modules/configurationModule/detail/local-samples/preformatted/shared/components/preformatted-editor-action-clear-template/preformatted-editor-action-clear-template'
import { TemplateRequestApplied } from '@/modules/configurationModule/detail/local-samples/preformatted/shared/components/preformatted-editor-action-synoptic-templates/preformatted-editor-action-synoptic-templates.model'
import PreformattedEditorActionSynopticTemplates from '@/modules/configurationModule/detail/local-samples/preformatted/shared/components/preformatted-editor-action-synoptic-templates/preformatted-editor-action-synoptic-templates.vue'
import PreformattedEditorActionTextTemplates from '@/modules/configurationModule/detail/local-samples/preformatted/shared/components/preformatted-editor-action-text-templates/preformatted-editor-action-text-templates.vue'
import PreformattedEditorActionTextTemplatesComponent from '@/modules/configurationModule/detail/local-samples/preformatted/shared/components/preformatted-editor-action-text-templates/preformatted-editor-action-text-templates'
import PreformattedEditorActionSynopticTemplatesComponent from '@/modules/configurationModule/detail/local-samples/preformatted/shared/components/preformatted-editor-action-synoptic-templates/preformatted-editor-action-synoptic-templates'
import { DescriptionResponseEditor, DescriptionResponseEnum, initialLocalPreformattedModalMacroState, LocalPreformattedDescriptionState, servicePreformattedEditor } from '@/modules/configurationModule/detail/local-samples/preformatted/shared/components/preformatted-editor/preformatted-editor.model'
import { Nullable } from '@/shared/types/app.types'
import { Vue, Component, Prop, Ref } from 'vue-property-decorator'
import ModalConfirmation from '@/components/modals/ModalConfirmation.vue'
import { isNil } from 'lodash'

@Component({
    components: {
        PreformattedEditorActionTextTemplates,
        PreformattedEditorActionSynopticTemplates,
        PreformattedEditorActionClearTemplates,
        ModalConfirmation
    }
})
export default class PreformattedEditor extends Vue {
    @Prop() readonly actionTitle!: string
    @Prop() readonly toastMessage!: string
    @Prop() readonly preformattedId!: number
    @Prop() readonly laboratoryId!: number
    @Prop() readonly service!: servicePreformattedEditor<DescriptionResponseEditor>
    @Prop() readonly broadcastChannelPostMessageFunction!: Function
    @Ref() readonly actionTextTemplates!: PreformattedEditorActionTextTemplatesComponent
    @Ref() readonly actionSynopticTemplates!: PreformattedEditorActionSynopticTemplatesComponent
    @Ref() readonly preformattedRichTextEditor!: PreformattedEditorActionClearTemplatesComponent
    @Ref() readonly onReplacingDescriptionText!: any
    state: LocalPreformattedDescriptionState

    constructor() {
        super()
        this.state = initialLocalPreformattedModalMacroState()
    }

    async mounted() {
        await this._getPreformattedTemplate()
    }

    get templateSelected(): Nullable<string> {
        let typeSelected: string = ''
        switch (this.state.description.descriptionType) {
        case 2:
            typeSelected = i18n.tc('configuration.sections.textTemplate', 1)
            return `${typeSelected}: ${this.state.description.template?.title}`
        case 3:
            typeSelected = i18n.tc('configuration.sections.synopticTemplate', 1)
            return `${typeSelected}: ${this.state.description.template?.title}`
        default:
            return null
        }
    }

    get canSave(): boolean {
        const hasErrors: boolean = this.errors.any()
        const isDirty = this.$_someFieldIsDirty(this.fields)
        let minSize: boolean
        switch (this.state.description.descriptionType) {
        case DescriptionResponseEnum.FREE_TEXT:
            minSize = true
            break
        case DescriptionResponseEnum.TEXT_TEMPLATE:
            minSize = !!this.state.description.text?.length
            break
        case DescriptionResponseEnum.SYNOPTIC_TEMPLATE:
            minSize = true
            break
        default: minSize = !!this.state.description.text?.length
        }
        return isDirty && !hasErrors && minSize
    }

    get canEdit(): boolean {
        return this.state.description.template?.textEditionAllowed!
    }

    get textTemplate(): string {
        return this.state.description.text!
    }

    get descriptionTemplate(): DescriptionResponseEditor {
        return this.state.description
    }

    get isTextNotEditable(): boolean {
        if (this.state.description.template?.id) {
            const isSynopticTemplate = this.state.description.descriptionType === DescriptionResponseEnum.SYNOPTIC_TEMPLATE
            const isTextTemplate = this.state.description.descriptionType === DescriptionResponseEnum.TEXT_TEMPLATE
            return (isSynopticTemplate || isTextTemplate)
        }
        return false
    }

    clean(): void {
        this.state.description = {
            ...this.state.description,
            descriptionType: DescriptionResponseEnum.FREE_TEXT,
            template: {
                id: null,
                textEditionAllowed: true,
                title: ''
            },
            optionsSelected: {},
            text: ''
        }
    }

    async updateText(newTemplate: TemplateRequestApplied): Promise<void> {
        if (!this._isSameTemplate(newTemplate)) {
            if (!this.state.description.text) {
                this._replaceDescription(newTemplate)
                this._setDirtyRichEditor()
            } else {
                await this.onReplacingDescriptionText.ask(() => {
                    this._replaceDescription(newTemplate)
                    this._setDirtyRichEditor()
                })
            }
        }
    }

    setText(newText: string): void {
        this.state.description.text = newText
    }

    openTemplatesModalIfDescriptionTextNotEditable(): void {
        if (!this.canEdit && this.isTextNotEditable) {
            if (this.state.description.descriptionType === DescriptionResponseEnum.TEXT_TEMPLATE) {
                this.actionTextTemplates.showTemplatesModal(true)
            } else if (this.state.description.descriptionType === DescriptionResponseEnum.SYNOPTIC_TEMPLATE) {
                this.actionSynopticTemplates.showTemplatesModal(true)
            }
        }
    }

    async updateTemplate(): Promise<void> {
        const request: DescriptionRequest = {
            descriptionType: this.state.description.descriptionType,
            originalConcurrencyUpdatedAt: this.state.description.originalConcurrencyUpdatedAt,
            text: this.state.description.text!,
            optionsSelected: this.state.description.optionsSelected,
            templateId: this.state.description.template?.id!
        }
        const [error, response] = await this.$_toCall(this.service.updatePreformatted(this.laboratoryId, this.preformattedId, request), true)
        if (error) {
            return
        }
        this.state.description.originalConcurrencyUpdatedAt = response.originalConcurrencyUpdatedAt
        this._showMessage(this.toastMessage)
        if (this.broadcastChannelPostMessageFunction) {
            this.broadcastChannelPostMessageFunction()
        }
    }

    private async _getPreformattedTemplate(): Promise<void> {
        const [err, response] = await this.$_toCall(this.service.getPreformatted(this.laboratoryId, this.preformattedId), true)
        if (err) {
            return
        }
        const isSynopticTemplate = response.descriptionType === DescriptionResponseEnum.SYNOPTIC_TEMPLATE
        const textEditionAllowed = !isSynopticTemplate
        const notExistsTextEditionAllowed = isNil(response.template?.textEditionAllowed)
        this.state.description = {
            ...this.state.description,
            id: response.id,
            descriptionType: response.descriptionType,
            optionsSelected: response.optionsSelected,
            text: response.text || '',
            originalConcurrencyUpdatedAt: response.originalConcurrencyUpdatedAt,
            template: {
                id: response.template?.id,
                title: response.template?.title,
                textEditionAllowed: notExistsTextEditionAllowed ? textEditionAllowed : response.template?.textEditionAllowed
            }
        }
        this._setDirtyRichEditor(false)
    }

    private _setDirtyRichEditor(value: boolean = true): void {
        this.$nextTick(() => {
            this.$validator.fields.find({ name: 'preformattedRichTextEditor' })?.setFlags({ dirty: value })
        })
    }

    private _showMessage(text: string = i18n.tc('configuration.toastMessages.updated', 1)): void {
        this.$_showToastSuccess(text)
    }

    private _replaceDescription(newTemplate: TemplateRequestApplied): void {
        this.state = {
            ...this.state,
            description: {
                ...this.state.description,
                optionsSelected: newTemplate.optionsSelected!,
                text: newTemplate.text,
                descriptionType: newTemplate.descriptionType,
                template: {
                    id: newTemplate.templateId!,
                    title: newTemplate.title,
                    textEditionAllowed: newTemplate.textEditingAllowed
                }
            }
        }
    }

    private static _parseTextOnlyContent(text: string): string {
        const regExpRemoveMultispacesAndMultilines = (/ {2}|\r\n|\n|\r/gm)
        const regExpRemoveTagsHTML = /<[^>]*>?/gm
        const nonNullText = isNil(text) ? '' : text
        return nonNullText
            .replace(regExpRemoveMultispacesAndMultilines, '')
            .replace(regExpRemoveTagsHTML, '')
    }

    private _isSameTemplate(newTemplate: TemplateRequestApplied): boolean {
        const isSameText = PreformattedEditor._parseTextOnlyContent(newTemplate.text) === PreformattedEditor._parseTextOnlyContent(this.state.description.text!)
        const isSameId = newTemplate.templateId === this.state.description.template?.id
        const isSameDescriptionType = newTemplate.descriptionType === this.state.description.descriptionType
        return isSameText && isSameId && isSameDescriptionType
    }
}
